// 3p
import {eventChannel} from 'redux-saga';
import {put, take, select, call} from 'redux-saga/effects';
import {takeEvery} from 'redux-saga';
import {browserHistory} from 'react-router'

import debugSetup from 'debug';
const debug = debugSetup('kiwi:saga');

// Fp
import * as c from '../../constants';
import {getTitle, getContent} from '../reducers/pages';
import {queries, mutations} from '../persistence';

/**
 */
export default function* rootSaga() {
  // wait for initial SET_AUTH_ADAPTER;
  yield take(c.SET_AUTH_ADAPTER);

  // get the auth adapter
  const auth = yield select(getAuth);

  // create a channel for auth events
  const chan = yield call(createAuthStateChannel, auth);

  // every auth event, trigger the proper action
  yield [
    takeEvery(chan, onAuthStateChanged),
    takeEvery(c.LOGIN, onLogin),
    takeEvery(c.LOGOUT, onLogout),
    takeEvery(c.CREATE_PAGE, onCreatePage),
    takeEvery(c.FETCH_PAGE, onFetchPage),
    takeEvery(c.FETCH_PAGE_INDEX, onFetchPageIndex),
    takeEvery(c.FETCH_DRAFT, onFetchDraft),
    takeEvery(c.DELETE_PAGE, onDeletePage),
    takeEvery(c.EDIT_PAGE, onEditPage)
  ];
}

function* onAuthStateChanged(user) {
  if (user === c.ANONYMOUS) {
    yield put({ type: c.ANONYMOUS });
  } else {
    yield put({ type: c.LOGGED_IN, user });

    const existing = yield select(state => state.channels.profile);

    if (existing) {
      if (existing.uid === user.uid) {
        debug(`re-using existing profile subscription`);
        return;
      } else {
        existing.channel.close();
      }
    }

    const db = yield select(getDb);

    // increment login count
    const loginCountRef = queries.profile.loginCount(db, user.uid);

    debug(`increment login count`);
    loginCountRef.transaction(loginCount => loginCount + 1);

    // subscribe to profile
    const channel = yield call(createProfileChannel, db, user.uid);

    yield put({ type: c.STORE_CHANNEL, name: 'profile', uid: user.uid, channel });

    yield takeEvery(channel, function* (action) {
      yield put(action);
    });
  }
}

function createProfileChannel(db, uid) {
  return eventChannel(cb => {
    debug(`subscribing to profile ${uid}`);

    const profileRef = queries.profile.byId(db, uid);

    profileRef.on('value', snapshot => {
      const profile = snapshot.val();
      cb({ type: c.RECEIVE_PROFILE, profile });
    });

    return () => {
      debug(`unsubscribing from profile ${uid}`);
      profileRef.off();
    }
  })
}

function getAuth(state) {
  return state.adapters.authentication;
}

function createAuthStateChannel(auth) {
  return eventChannel(cb => {
    auth.onAuthStateChanged(user => {
      if (user) {
        cb(user);
      } else {
        cb(c.ANONYMOUS);
      }
    });

    return () => {
      console.log('unsubscribe not implemented');
    }
  });
}

function* onLogin() {
  const auth = yield select(getAuth);

  try {
    yield call(auth.signIn);
    yield call(browserHistory.push, '/pages');
  } catch (error) {
    console.log('Error in sign in');
    console.log(error);
  }
}

function* onLogout() {
  const auth = yield select(getAuth);

  try {
    yield call(auth.signOut);
    yield call(browserHistory.push, '/');
  } catch (error) {
    console.log('Error in sign out');
    console.log(error);
  }
}

function* onCreatePage() {
  const draft = yield select(getDraft);
  const db = yield select(getDb);

  // Create identifiers
  const projectId = 0;
  const pageId = queries.pages.new(db);

  yield call(createVersion, projectId, pageId, draft);
}

function getDraft(state) {
  return state.draft;
}

function getDb(state) {
  return state.adapters.db;
}

function* onFetchPage({pageId}) {
  // Terminate any existing subscriptions
  const existing = yield select(state => state.channels.fetchPage);

  if (existing) {
    if (existing.pageId === pageId) {
      debug('re-using existing subscription');
      return;
    } else {
      existing.channel.close();
    }
  }

  const db = yield select(getDb);
  const projectId = 0;
  const channel = yield call(createPageChannel, db, projectId, pageId);

  yield put({ type: c.STORE_CHANNEL, name: 'fetchPage', pageId, channel });
  yield takeEvery(channel, function* (action) {
    yield put(action);
  });
}

function createPageChannel(db, projectId, pageId) {
  return eventChannel(cb => {
    debug(`subscribing to page ${pageId}`);

    let pageRef, versionRef, contentRef;

    // Subscribe to page
    pageRef = queries.pages.byId(db, projectId, pageId);
    pageRef.on('value', snapshot => {
      const page = snapshot.val();
      cb({ type: c.RECEIVE_PAGE, key: pageId, value: page });

      // Subscribe to version
      const versionId = page.versionId;
      versionRef = queries.versions.byId(db, pageId, versionId);
      versionRef.on('value', snapshot => cb({ type: c.RECEIVE_VERSION, key: versionId, value: snapshot.val() }));

      // Subscribe to content
      contentRef = queries.content.byVersionId(db, versionId);
      contentRef.on('value', snapshot => cb({ type: c.RECEIVE_CONTENT, key: versionId, value: snapshot.val() }));
    });

    // Unsubscribe
    return () => {
      debug(`unsubscribing from ${pageId}`);

      pageRef.off();

      if (versionRef) {
        versionRef.off();
      }

      if (contentRef) {
        contentRef.off();
      }
    }
  });
}

function* onFetchPageIndex() {
  const existing = yield select(state => state.channels.fetchPageIndex);

  if (existing) {
    return debug(`already subscribed to page index`);
  }

  const db = yield select(getDb);
  const projectId = 0;
  const channel = yield call(createPageIndexChannel, db, projectId);

  yield put({ type: c.STORE_CHANNEL, name: 'fetchPageIndex', channel });
  yield takeEvery(channel, function* (action) {
    yield put(action);
  });
}

function createPageIndexChannel(db, projectId) {
  return eventChannel(cb => {
    debug(`subscribing to page index`);

    const pagesRef = queries.pages.byProjectId(db, projectId);

    /**
     * Contains versionRefs indexed by pageId. Used for when child_changed triggers on a page to remove the old
     * version subscription and create a new one
     */
    let versionRefsByPageId = {};

    pagesRef.on('child_added', snapshot => {
      const pageId = snapshot.key;
      const page = snapshot.val();

      cb({ type: c.RECEIVE_PAGE_INDEX, key: pageId, value: page });

      // Subscribe to version
      const versionId = page.versionId;
      const versionRef = queries.versions.byId(db, pageId, versionId);
      versionRef.on('value', snapshot => cb({ type: c.RECEIVE_VERSION, key: versionId, value: snapshot.val() }));

      // Store ref here for a sec
      versionRefsByPageId[pageId] = versionRef;
    });

    pagesRef.on('child_changed', snapshot => {
      const pageId = snapshot.key;
      const page = snapshot.val();

      cb({ type: c.RECEIVE_PAGE_INDEX, key: pageId, value: page });

      // Redo versionRef if needed
      const oldVersionRef = versionRefsByPageId[pageId];

      if (oldVersionRef) {
        oldVersionRef.off();
      }

      // Re-subscribe to version
      const versionId = page.versionId;
      const versionRef = queries.versions.byId(db, pageId, versionId);
      versionRef.on('value', snapshot => cb({ type: c.RECEIVE_VERSION, key: versionId, value: snapshot.val() }));

      // Overwrite old ref with new one
      versionRefsByPageId[pageId] = versionRef;
    });

    pagesRef.on('child_removed', snapshot => {
      const pageId = snapshot.key;
      cb({ type: c.REMOVE_PAGE_INDEX, key: pageId });
    });

    // Unsubscribe
    return () => {
      debug(`unsubscribing from page index`);

      pagesRef.off();

      Object.keys(versionRefsByPageId).each(pageId => {
        const ref = versionRefsByPageId[pageId];
        ref.off();
      });
    }
  });
}

function* onFetchDraft({pageId}) {
  const db = yield select(getDb);
  const projectId = 0;

  // Load from cache if we have it
  yield call(loadDraft, pageId);

  // Get single snapshot of page/version/content
  const actions = yield call(getPageSnapshot, db, projectId, pageId);

  // Put data in store
  yield actions.map(action => put(action));

  // After data loaded, load draft again
  yield call(loadDraft, pageId);
}

function* loadDraft(pageId) {
  // Get title from store
  const title = yield select(state => getTitle(state.pages, pageId))

  // Put it in draft
  yield put({ type: c.DRAFT_TITLE_CHANGE, title });

  // Get content from the store
  const content = yield select(state => getContent(state.pages, pageId))

  // Put it in draft
  yield put({ type: c.DRAFT_CONTENT_CHANGE, content });
}

/**
 * Return an array of actions to create a page based on pageId
 */
function getPageSnapshot(db, projectId, pageId) {
  const pageRef = queries.pages.byId(db, projectId, pageId);

  return pageRef.once('value')
    .then(snapshot => {
      const page = snapshot.val();
      const pageAction = { type: c.RECEIVE_PAGE, key: pageId, value: page };

      const versionId = page.versionId;
      const versionRef = queries.versions.byId(db, pageId, versionId);
      const contentRef = queries.content.byVersionId(db, versionId);

      return Promise.all([versionRef.once('value'), contentRef.once('value')])
        .then(values => {
          return [
            pageAction,
            { type: c.RECEIVE_VERSION, key: versionId, value: values[0].val() },
            { type: c.RECEIVE_CONTENT, key: versionId, value: values[1].val() }
          ]
        });
    });
}

/**
 * Deletes a page given a pageId
 *
 * - confirm with user
 * - move pages/projectId/pageId to pages_deleted/projectId/pageId
 * - redirect to pages index
 */
function* onDeletePage({pageId}) {
  const confirmed = window.confirm('Are you sure?');

  if (!confirmed) {
    return;
  }

  const db = yield select(getDb);
  const projectId = 0;

  // Terminate any existing subscriptions
  const existing = yield select(state => state.channels.fetchPage);
  existing.channel.close();

  mutations.deletePage(db, projectId, pageId);

  yield call(browserHistory.push, '/pages');
}

/**
 * Creates a new version of a page given a pageId
 */
function* onEditPage({pageId}) {
  const draft = yield select(getDraft);
  const projectId = 0;
  yield call(createVersion, projectId, pageId, draft);
}

function* createVersion(projectId, pageId, draft) {
  const db = yield select(getDb);
  const {page, version, content} = mutations.createVersion(db, projectId, pageId, draft);

  // Eagerly update content
  yield put({ type: c.RECEIVE_PAGE, key: pageId, value: page });
  yield put({ type: c.RECEIVE_VERSION, key: version.id, value: version });
  yield put({ type: c.RECEIVE_CONTENT, key: version.id, value: content });

  // Redirect to page
  yield call(browserHistory.push, `/pages/${pageId}`);
}


/** WEBPACK FOOTER **
 ** ./src/store/sagas/index.js
 **/